import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { SearchIcon } from '@heroicons/react/outline';
import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  giftOrderSearchDataAtomFamily,
  giftOrderSearchStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import {
  GiftOrderSearchParams,
  useGiftOrderSearchHook,
} from '@shared/api/giftOrder/giftOrder.hook';
import {
  GiftOrderDocStatus,
  GiftOrderForAdmin,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Loading } from '@shared/components/Loading';
import { formatPrice } from '@shared/utils/formatPrice';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { Link } from 'react-router-dom';

import { CouponDetailModal } from '../components/CouponDetailModal';

// 결제 상태 한글 변환
const paymentStatusLabel: Record<GiftOrderPaymentStatus, string> = {
  [GiftOrderPaymentStatus.NotPaid]: '미결제',
  [GiftOrderPaymentStatus.Waiting]: '입금대기',
  [GiftOrderPaymentStatus.Paid]: '결제완료',
  [GiftOrderPaymentStatus.Confirmed]: '확정',
  [GiftOrderPaymentStatus.CancelRequested]: '취소요청',
  [GiftOrderPaymentStatus.Canceled]: '취소됨',
};

// 날짜 포맷
const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// 쿠폰 현황 계산 (coupons virtual 기반)
const getCouponStats = (order: GiftOrderForAdmin) => {
  const coupons = order.coupons;
  if (!coupons || coupons.length === 0) return null;

  const totalCount = coupons.length;
  const unusedCount = coupons.filter(
    (c) =>
      c.redeemStatus !== GiftCouponRedeemStatus.Delivered &&
      c.redeemStatus !== GiftCouponRedeemStatus.Refunded &&
      c.redeemStatus !== GiftCouponRedeemStatus.Canceled,
  ).length;
  return { unusedCount, totalCount };
};

// 주문 행 컴포넌트
const OrderRow: FC<{
  order: GiftOrderForAdmin;
  onCouponClick: (coupons: GiftCouponSummary[]) => void;
}> = ({ order, onCouponClick }) => {
  const memberName = order.bugo?.member?.fullName || '-';
  const memberPhone = order.bugo?.member?.phoneNumber || '-';
  const deceasedName = order.bugo?.fevent?.deceasedInfo?.name || '-';
  const bugoBrandName =
    typeof order.bugoBrand === 'object' ? order.bugoBrand.bugoAgency?.corpName : '-';
  const totalItems = order.orderDetails.reduce((sum, d) => sum + d.quantity, 0);
  const firstItemName = order.orderDetails[0]?.giftItemEmbed.name || '-';
  const orderName =
    order.orderDetails.length > 1
      ? `${firstItemName} 외 ${order.orderDetails.length - 1}건`
      : firstItemName;
  const isDeleted = order.docStatus === GiftOrderDocStatus.Deleted;
  const couponStats = getCouponStats(order);

  return (
    <tr className={`hover:bg-gray-50 ${isDeleted ? 'bg-red-50' : ''}`}>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {formatDate(order.paymentDetail.purchasedAt || order.createdAt)}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <Link
          to={`/gift-seller/order/${order._id}`}
          className="font-medium text-indigo-600 hover:text-indigo-900"
        >
          {orderName}
        </Link>
        <p className="text-xs text-gray-400">수량: {totalItems}개</p>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
        {formatPrice(order.paymentDetail.paymentByCash)}원
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        <p>{memberName}</p>
        <p className="text-xs">{phoneNumberStringFormator(memberPhone)}</p>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {deceasedName}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {bugoBrandName}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        {couponStats && order.coupons && order.coupons.length > 0 ? (
          <button
            onClick={() => onCouponClick(order.coupons ?? [])}
            className={`inline-flex cursor-pointer rounded-full px-2 text-xs font-semibold leading-5 transition-opacity hover:opacity-75 ${
              couponStats.unusedCount === 0
                ? 'bg-gray-100 text-gray-500'
                : 'bg-blue-100 text-blue-800'
            }`}
          >
            {couponStats.unusedCount}/{couponStats.totalCount}
          </button>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <span
          className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
            order.paymentDetail.status === GiftOrderPaymentStatus.Paid ||
            order.paymentDetail.status === GiftOrderPaymentStatus.Confirmed
              ? 'bg-green-100 text-green-800'
              : order.paymentDetail.status === GiftOrderPaymentStatus.Canceled
              ? 'bg-red-100 text-red-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {paymentStatusLabel[order.paymentDetail.status] || order.paymentDetail.status}
        </span>
        {isDeleted && (
          <span className="ml-1 inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">
            삭제됨
          </span>
        )}
      </td>
    </tr>
  );
};

export const GiftOrderSearchPage: FC = function GiftOrderSearchPage() {
  const [modalCoupons, setModalCoupons] = useState<GiftCouponSummary[] | null>(null);

  // 검색 입력 상태
  const [phoneNumber, setPhoneNumber] = useState('');
  const [receiverName, setReceiverName] = useState('');

  // 검색 실행 상태
  const [searchKey, setSearchKey] = useState('');
  const [searchParams, setSearchParams] = useState<GiftOrderSearchParams | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // 검색 훅 (searchKey가 있을 때만 실행)
  useGiftOrderSearchHook(searchKey, searchParams || {}, !searchKey || !searchParams);

  const status = useRecoilValue(giftOrderSearchStatusAtomFamily(searchKey));
  const searchResults = useRecoilValue(giftOrderSearchDataAtomFamily(searchKey));

  // 검색 실행
  const handleSearch = useCallback(() => {
    if (!phoneNumber && !receiverName) {
      alert('검색 조건을 하나 이상 입력해주세요.');
      return;
    }

    const params: GiftOrderSearchParams = {};
    if (phoneNumber) params.phoneNumber = phoneNumber.replace(/[^0-9]/g, '');
    if (receiverName) params.receiverName = receiverName;

    // 검색 키 생성 (파라미터 기반)
    const key = JSON.stringify(params);
    setSearchKey(key);
    setSearchParams(params);
    setHasSearched(true);
  }, [phoneNumber, receiverName]);

  // Enter 키 처리
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  // 검색 결과
  const results = useMemo(() => {
    if (!searchResults) return [];
    return searchResults;
  }, [searchResults]);

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">주문 검색</h1>
        <p className="mt-1 text-sm text-gray-500">
          고객 응대용 - 핸드폰번호, 주문자 성함으로 검색
        </p>
      </div>

      {/* 검색 폼 */}
      <div className="mb-6 rounded-lg bg-white p-4 shadow">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">핸드폰번호</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="010-1234-5678"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">주문자 성함</label>
            <input
              type="text"
              value={receiverName}
              onChange={(e) => setReceiverName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="홍길동"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              className="flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <SearchIcon className="h-4 w-4" />
              검색
            </button>
          </div>
        </div>
      </div>

      {/* 검색 결과 */}
      {hasSearched && (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          {status === 'loading' ? (
            <div className="center-box py-12">
              <Loading />
            </div>
          ) : (
            <>
              <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-sm text-gray-500">검색 결과: {results.length}건</p>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      결제일시
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      상품명
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      결제금액
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      주문자
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      고인명
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      상조
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      쿠폰
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      상태
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {results.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                        검색 결과가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    results.map((order) => (
                      <OrderRow
                        key={order._id}
                        order={order}
                        onCouponClick={setModalCoupons}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* 검색 전 안내 */}
      {!hasSearched && (
        <div className="rounded-lg bg-white p-12 text-center shadow">
          <SearchIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">주문 검색</h3>
          <p className="mt-1 text-sm text-gray-500">
            검색 조건을 입력하고 검색 버튼을 눌러주세요.
          </p>
        </div>
      )}

      {modalCoupons && (
        <CouponDetailModal coupons={modalCoupons} onClose={() => setModalCoupons(null)} />
      )}
    </div>
  );
};
